import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiInnerWrapComponent, QaiLoadingComponent } from '@qai/ui';
import { AccountTeamSelectorComponent } from 'src/app/pages/reporting-dashboard/account-team-selector/account-team-selector.component';
import { DashboardTabsComponent } from 'src/app/pages/reporting-dashboard/dashboard-tabs/dashboard-tabs.component';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { AccountOption, IAccountsByCustomer } from 'src/app/interfaces/self-serve/iaccounts-by-customer';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { ICustomerAccountPayload } from 'src/app/interfaces/reportingV2/icustomer-account-payload';
import { AccountEntityUtil } from 'src/app/utils/account-entity-util';
import { ReportingLocalStorageUtil } from 'src/app/utils/reporting-localstorage.util';
import { EntityOption } from 'src/app/interfaces/reportingV2/ientity-option';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';
import { ReportingNavigationResetService } from 'src/app/services/reporting-dashboard/reporting-navigation-reset.service';

@Component({
  selector: 'app-reporting-dashboard',
  templateUrl: './reporting-dashboard.component.html',
  styleUrls: ['./reporting-dashboard.component.scss'],
  providers: [
    { provide: TRANSLOCO_SCOPE, useValue: 'reportingV2' }
  ],
  standalone: true,
  imports: [
    CommonModule,
    QaiInnerWrapComponent,
    QaiLoadingComponent,
    AccountTeamSelectorComponent,
    DashboardTabsComponent,
    TranslocoModule
  ]
})
export class ReportingDashboardComponent implements OnInit {
  accountOptions: AccountOption[] = [];
  selectedAccount: AccountOption | null = null;
  isLoadingAccounts = false;
  multipleCustomers: ICustomerAccountPayload[] = [];
  entityOptions: EntityOption[] = [];
  selectedEntities: string[]  = [];
  isLoadingEntities = false;
  isTranslationLoaded = false;
  multiCustomerAccount: IAccountsByCustomer[] = [];
  isTeamAvailable: boolean = false;
  isLoadingTeam: boolean = false;
  isNetworkCallInprogress: boolean = false;

  constructor(
    private dashboardService: ReportingDashboardService,
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private amplitudeService: AmplitudeService,
    private readonly reportingNavigationResetService: ReportingNavigationResetService
  ) { }

  ngOnInit(): void {
    this.reportingNavigationResetService.init();
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
    });
    this.loadAccounts();

    // Track pageview event
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.REPORTINGDASHBOARD_PAGEVIEW);
  }

  private loadAccounts(): void {
    this.isLoadingAccounts = true;

    this.dashboardService.getAccounts(withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (accountsByCustomer: IAccountsByCustomer[]) => {
        this.multiCustomerAccount = accountsByCustomer;

        if (accountsByCustomer.length > 1) {
          this.multipleCustomers = this.createCustomerAccountPayload();
          console.log('Customer-Account Payload:', this.multipleCustomers);
        }

        this.accountOptions = AccountEntityUtil.formatAccounts(accountsByCustomer);

        const storedAccountUuid = ReportingLocalStorageUtil.getStoredAccountUuid();
        this.selectedAccount = AccountEntityUtil.getDefaultSelectedAccount(this.accountOptions, storedAccountUuid);

        if (this.selectedAccount) {
          ReportingLocalStorageUtil.setStoredAccountUuid(this.selectedAccount.value);
          this.loadTeams(this.selectedAccount.value);
        }

        this.isLoadingAccounts = false;
      },
      error: (error) => {
        console.error('Failed to load accounts:', error);
        this.isLoadingAccounts = false;
      }
    });
  }

  private loadTeams(accountUuid: string): void {
    this.isLoadingEntities = true;
    this.isTeamAvailable = true;
    this.isLoadingTeam = true;
    this.isNetworkCallInprogress = true;

    this.dashboardService.getTeams(accountUuid, withDemoModule(AppConstants.NEW_REPORTING_UI)).subscribe({
      next: (entities) => {
        this.isLoadingTeam = false;
        this.isNetworkCallInprogress = false;
        if (entities.length === 0) {
          this.isTeamAvailable = false;
          this.entityOptions = [];
          this.selectedEntities = [];
          ReportingLocalStorageUtil.setStoredTeamsFilter([], accountUuid);
          this.isLoadingEntities = false;
          return;
        }
        this.isTeamAvailable = true;

        this.entityOptions = AccountEntityUtil.formatEntities(entities);

        const storedTeamsFilter = ReportingLocalStorageUtil.getStoredTeamsFilter(accountUuid);
        this.selectedEntities = storedTeamsFilter ?? [];

        if (!storedTeamsFilter || storedTeamsFilter.length === 0) {
          ReportingLocalStorageUtil.setStoredTeamsFilter(this.selectedEntities, accountUuid);
        }

        this.isLoadingEntities = false;
      },
      error: (error) => {
        console.error('Failed to load entities:', error);
        this.isLoadingEntities = false;
        this.isLoadingTeam = false;
        this.isNetworkCallInprogress = false;
      }
    });
  }

  onEntityChange(selectedValues: string[]): void {
    this.selectedEntities = selectedValues;
    if (this.selectedAccount?.value) {
      ReportingLocalStorageUtil.setStoredTeamsFilter(selectedValues, this.selectedAccount.value);
    }
  }


  onAccountChange(selectedValue: string): void {
    this.selectedAccount = AccountEntityUtil.findAccountByUuid(this.accountOptions, selectedValue);
    ReportingLocalStorageUtil.setStoredAccountUuid(selectedValue);
    this.loadTeams(selectedValue);
  }

  createCustomerAccountPayload(): ICustomerAccountPayload[] {
    return AccountEntityUtil.createCustomerAccountPayload(this.multiCustomerAccount);
  }
}
